export interface SettlementPeriod {
  startDate: string;
  endDate: string;
  clamped: boolean;
}

interface CalendarDateParts {
  year: number;
  month: number;
  day: number;
}

function isLeapYear(year: number) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number) {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function formatDate({ year, month, day }: CalendarDateParts) {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(
    day
  ).padStart(2, "0")}`;
}

function parseDate(value: string): CalendarDateParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new Error("유효한 YYYY-MM-DD 날짜가 아닙니다.");
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month)
  ) {
    throw new Error("유효한 YYYY-MM-DD 날짜가 아닙니다.");
  }
  return { year, month, day };
}

function monthAt(year: number, month: number, offset: number) {
  const index = year * 12 + month - 1 + offset;
  return {
    year: Math.floor(index / 12),
    month: (index % 12) + 1
  };
}

function previousDay(value: CalendarDateParts): CalendarDateParts {
  if (value.day > 1) return { ...value, day: value.day - 1 };
  const previous = monthAt(value.year, value.month, -1);
  return {
    ...previous,
    day: daysInMonth(previous.year, previous.month)
  };
}

export function clampDayToMonth(year: number, month: number, requestedDay: number) {
  if (!Number.isInteger(year) || year < 1 || month < 1 || month > 12) {
    throw new Error("유효한 연도와 월이 아닙니다.");
  }
  if (!Number.isInteger(requestedDay) || requestedDay < 1 || requestedDay > 31) {
    throw new Error("정산 기준일은 1~31 사이여야 합니다.");
  }
  return Math.min(requestedDay, daysInMonth(year, month));
}

export function isCalendarDate(value: string) {
  try {
    parseDate(value);
    return true;
  } catch {
    return false;
  }
}

export function buildSettlementPeriods(startDate: string, count = 3): SettlementPeriod[] {
  const start = parseDate(startDate);
  if (!Number.isInteger(count) || count < 1 || count > 24) {
    throw new Error("정산 미리보기 개수는 1~24 사이여야 합니다.");
  }
  const requestedDay = start.day;

  return Array.from({ length: count }, (_, offset) => {
    const currentMonth = monthAt(start.year, start.month, offset);
    const nextMonth = monthAt(start.year, start.month, offset + 1);
    const currentDay =
      offset === 0
        ? start.day
        : clampDayToMonth(currentMonth.year, currentMonth.month, requestedDay);
    const nextDay = clampDayToMonth(nextMonth.year, nextMonth.month, requestedDay);
    const end = previousDay({ ...nextMonth, day: nextDay });
    return {
      startDate: formatDate({ ...currentMonth, day: currentDay }),
      endDate: formatDate(end),
      clamped: nextDay !== requestedDay
    };
  });
}
